/**
 * Her, full screen — what tapping the greeting opens now instead of
 * `PasJaSheet`'s bottom sheet.
 *
 * The day ring itself becomes her: measured live off `Ring.tsx`'s real DOM
 * (see `BootSplash.tsx` for the reference implementation of "measure a real
 * element, then imperatively morph a clone toward it"), a clone rises into a
 * thin violet halo while the real ring quietly keeps rendering underneath the
 * backdrop, and the big mark fades in where the clone settles. Once that
 * hands off, this is the same "apartment" `PasJaSheet` used to show — the
 * remark, „Jos diena", the conversation, the doors to her other rooms — just
 * full screen and staged in rather than dropped in.
 *
 * The morph is driven imperatively through refs, not React state, for the
 * same reason `BootSplash` is: a re-render per frame would fight the
 * transitions it is trying to run. `skipRef` holds whichever direction is
 * currently in flight so a tap on the overlay can jump straight to that
 * direction's resting state instead of waiting it out.
 */

import { REPEAT_WINDOW_MS } from '@gramas/shared'
import { Sheet, useBackLayer } from '@gramas/ui'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePresence } from '../presence/store.js'
import { dayContext } from '../sass/context.js'
import { dailyLine, sassLine } from '../sass/engine.js'
import { closeGate, openGate } from '../shell/toastStore.js'
import { GhostReveal } from '../stage/GhostReveal.js'
import { TrophyCabinet } from '../trophies/TrophyCabinet.js'
import { type AwayState, awayNow, onAwayChange, refreshAway } from './away.js'
import { type ChatPose, ChatThread } from './ChatThread.js'
import {
  type ChatDayRecord,
  loadChatDay,
  makeDayTransport,
  markChatEnded,
  saveChatDay,
} from './chatDay.js'
import type { ChatTurn } from './chatTransport.js'
import { chipsFor } from './chips.js'
import { herHour, herToday } from './clock.js'
import { DoorClosed } from './DoorClosed.js'
import { DossierSheet } from './DossierSheet.js'
import { resolveFollowUpDone, takeFollowUpAsk } from './followups.js'
import { affectionNow, clearCooled } from './interaction.js'
import { personalFor, takePersonalLine } from './lines.js'
import { GlitimeMark } from './Mark.js'
import { PromisesSheet } from './PromisesSheet.js'
import { petHer } from './pet.js'
import { claimPresence, releasePresence } from './presenceRegistry.js'
import { TONE_STATUS } from './tone.js'
import { beginCloseZone, closeDoor, closeZone, useZoneState, zoneSettled } from './zone.js'

const EASE = 'var(--ease-out-quick)'
/** The halo's resting size — matches `Ring.tsx`'s own default, so the clone
 * never has to scale, only move and recolour. */
const HALO_SIZE = 168
/** Noticeably bigger than any other mark in the app: this is the one the
 * feature is named for. */
const BIG_MARK_SIZE = 112

/** Past this, the finger on the hero is dragging rather than pressing —
 * same shape as `Sheet.tsx`'s own gesture, which is module-private and so
 * not importable; these are its values, kept in step by hand. */
const DRAG_START_PX = 6
const DISMISS_TRAVEL_PX = 96
const DISMISS_FLICK_PX = 28
const DISMISS_FLICK_SPEED = 0.55

interface RingSnapshot {
  rect: DOMRect
  radius: number
  strokeWidth: number
  dasharray: string
  color: string
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** The live day ring, exactly as `BootSplash.tsx` finds it — same selector,
 * same "second circle is the arc" contract from `Ring.tsx`. Null for any
 * reason (not on screen, not painted yet) rather than throwing. */
function measureRing(): RingSnapshot | null {
  const svg = document.querySelector('[data-day-ring] svg')
  if (!(svg instanceof SVGSVGElement)) return null
  const rect = svg.getBoundingClientRect()
  if (rect.width === 0) return null
  const arc = svg.querySelector('circle:last-of-type')
  if (!(arc instanceof SVGCircleElement)) return null
  const style = getComputedStyle(arc)
  const strokeWidth = Number.parseFloat(style.strokeWidth)
  return {
    rect,
    radius: arc.r.baseVal.value,
    strokeWidth: Number.isFinite(strokeWidth) ? strokeWidth : 10,
    dasharray: style.strokeDasharray,
    color: style.stroke,
  }
}

function makeTimers() {
  const timers = new Set<ReturnType<typeof setTimeout>>()
  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const id = setTimeout(() => {
        timers.delete(id)
        resolve()
      }, ms)
      timers.add(id)
    })
  const clearAll = () => {
    for (const id of timers) clearTimeout(id)
    timers.clear()
  }
  return { sleep, clearAll }
}

export function HerZone() {
  const phase = useZoneState((state) => state.phase)
  const openedDate = useZoneState((state) => state.openedDate)
  const doorOpen = useZoneState((state) => state.doorOpen)
  const presence = usePresence()

  const [snap, setSnap] = useState<RingSnapshot | null>(null)
  const [backdropVisible, setBackdropVisible] = useState(false)
  const [contentVisible, setContentVisible] = useState(false)
  const [showMark, setShowMark] = useState(false)
  const [pose, setPose] = useState<ChatPose>(null)

  const [remark, setRemark] = useState<string | null>(null)
  const [herDay, setHerDay] = useState<string | null>(null)
  const [chipLabels, setChipLabels] = useState<string[] | undefined>(undefined)
  // undefined: not drawn yet. Same trick `PasJaSheet` used — the thread waits
  // for this to settle so its first render is already its final one.
  const [opener, setOpener] = useState<string | null | undefined>(undefined)
  const [openerFollowUpId, setOpenerFollowUpId] = useState<string | null>(null)
  const [dayRecord, setDayRecord] = useState<ChatDayRecord | null>(null)
  const [away, setAway] = useState<AwayState | null>(null)

  const [drag, setDrag] = useState(0)

  const cloneWrapRef = useRef<HTMLDivElement>(null)
  const cloneArcRef = useRef<SVGCircleElement>(null)
  const cloneTrackRef = useRef<SVGCircleElement>(null)
  const haloSlotRef = useRef<HTMLDivElement>(null)
  /** The last successful measurement, so a later exit knows the halo's
   * radius even if the real ring cannot be re-measured for some reason. */
  const lastSnapRef = useRef<RingSnapshot | null>(null)
  /** Whichever direction is currently in flight hands its jump-to-rest here,
   * for a tap on the overlay to call. */
  const skipRef = useRef<() => void>(() => {})
  /**
   * Bumped by whichever morph starts most recently, enter or exit. Every
   * checkpoint in both async sequences below compares against its own
   * snapshot of this rather than a private `cancelled` flag: `beginCloseZone`
   * can land mid-arrival (Back, or the close button, tapped while she is
   * still on her way in), and the enter effect has no `phase` dependency to
   * unwind it with a cleanup when that happens. A later run starting simply
   * invalidates whatever the earlier one is still doing, from either effect,
   * however far into its own timers it already was.
   */
  const runIdRef = useRef(0)
  const dayRecordRef = useRef<ChatDayRecord | null>(null)
  /** Set when a paid price just opened the door, so the next opener is a thaw. */
  const thawed = useRef(false)
  const gestureRef = useRef<{
    id: number
    from: number
    at: number
    y: number
    speed: number
    captured: boolean
  } | null>(null)

  const [transport] = useState(() => makeDayTransport())

  // Presence, the toast gate and Back — for as long as this is mounted at
  // all, not just once it has settled. A visit that is still arriving should
  // already dim the greeting strip and hold ordinary toasts behind it.
  useEffect(() => {
    closeGate()
    const claim = claimPresence('zone')
    return () => {
      openGate()
      releasePresence(claim)
    }
  }, [])

  useBackLayer(true, beginCloseZone)

  // The cached transcript (still within its 24h window), once. `persistTurns`
  // below writes through a ref rather than back into this state, so a
  // message sent mid-visit never re-triggers the opener effect below.
  useEffect(() => {
    let cancelled = false
    void loadChatDay().then((record) => {
      if (cancelled) return
      dayRecordRef.current = record
      setDayRecord(record)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Whether she is in. The cached answer draws the door immediately (offline
  // included); the refresh catches a door she closed on the other phone, and
  // the subscription catches her hour arriving while he is standing here.
  useEffect(() => {
    setAway(awayNow())
    void refreshAway().then(() => setAway(awayNow()))
    return onAwayChange(() => setAway(awayNow()))
  }, [])

  // The remark and „Jos diena", pinned per day rather than drawn per mount.
  // They used to reroll every time the zone opened, so a second visit in the
  // same minute met a different her — `dailyLine` is the same pinning the
  // greeting strip has always used.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design.
  useEffect(() => {
    const hour = herHour()
    const date = herToday()
    setRemark(dailyLine('presence_remark', date, { hour }))
    setHerDay(
      presence.herLife == null
        ? null
        : (dailyLine('herday_detail', date, { hour, herLife: presence.herLife }) ??
            dailyLine('greeting', date, { hour, herLife: presence.herLife })),
    )
  }, [])

  // The chips, with the day's real numbers behind them. A thin context here
  // was why the over-budget chips could never appear in the zone: their
  // `fits` predicates read `isOver` and `entryKcal`, and neither was passed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design.
  useEffect(() => {
    let cancelled = false
    void dayContext(herToday()).then((day) => {
      if (cancelled) return
      setChipLabels(
        chipsFor(
          {
            ...day,
            hour: herHour(),
            mood: presence.mood,
            daysSinceLastLog: presence.daysSinceLastLog,
          },
          affectionNow() === 'cooled',
          Math.random,
        ).map((chip) => chip.label),
      )
    })
    return () => {
      cancelled = true
    }
  }, [])

  // The opener: only on a day's first open. `PasJaSheet`'s exact ranking —
  // a due follow-up outranks a personal callback — but only once the day's
  // transcript has actually loaded and turned out to be empty; a reopened
  // conversation gets no fresh hello.
  useEffect(() => {
    if (dayRecord == null || away != null) return
    if (dayRecord.turns.length > 0) {
      setOpener(null)
      setOpenerFollowUpId(null)
      return
    }
    // The first visit after a door she closed herself: she opens, not him,
    // and she says so before anything else. Outranks the follow-up — asking
    // about his exam as though nothing happened would be a different person.
    if (thawed.current) {
      thawed.current = false
      setOpenerFollowUpId(null)
      setOpener(sassLine('chat_thaw'))
      return
    }
    const followUp = takeFollowUpAsk()
    if (followUp != null) {
      setOpener(followUp.ask)
      setOpenerFollowUpId(followUp.id)
      return
    }
    setOpenerFollowUpId(null)
    const candidate = takePersonalLine(personalFor('chat_opener'), REPEAT_WINDOW_MS, Math.random)
    setOpener(candidate?.text ?? null)
  }, [dayRecord, away])

  const persistTurns = (turns: readonly ChatTurn[]) => {
    const base = dayRecordRef.current ?? { updatedAt: Date.now(), turns: [], closedThrough: 0 }
    const next: ChatDayRecord = { ...base, turns: [...turns], updatedAt: Date.now() }
    dayRecordRef.current = next
    void saveChatDay(next)
  }

  // ── The enter morph — closed's absence became 'entering' the instant
  // before this mounted, so this is the whole visit's arrival. ──
  useEffect(() => {
    const myRun = ++runIdRef.current
    const { sleep, clearAll } = makeTimers()

    // Back (or the close button) can call `beginCloseZone()` mid-arrival,
    // which the store honours (`entering -> leaving`) without waiting for
    // this to finish, and this effect has no `phase` dependency to unwind
    // it with a cleanup when that happens. Every checkpoint below re-reads
    // the store's live phase directly (not a snapshot the exit morph could
    // already have moved past) rather than trusting a private `cancelled`
    // flag alone — a stale "yes, still entering" here would have this go on
    // setting content visible and the mark showing after the exit morph
    // already started reversing them. `runIdRef` closes the same gap from
    // the other side: it is what the exit morph invalidates this run with,
    // for the checkpoint after `zoneSettled()` where 'entering' is no
    // longer the phase to expect.
    const stillMyTurn = (expected: 'entering' | 'open') =>
      runIdRef.current === myRun && useZoneState.getState().phase === expected

    const finishInstantly = () => {
      if (!stillMyTurn('entering')) return
      clearAll()
      setSnap(null)
      setBackdropVisible(true)
      setShowMark(true)
      setContentVisible(true)
      zoneSettled()
    }
    skipRef.current = finishInstantly

    const run = async () => {
      const reduced = prefersReducedMotion()
      const measured = reduced ? null : measureRing()

      if (reduced || measured == null) {
        finishInstantly()
        return
      }
      lastSnapRef.current = measured
      setSnap(measured)
      // Two frames: the clone has to be painted at the ring's own position
      // before anything asks it to move, or the move is the first thing
      // anyone ever sees it do.
      await sleep(32)
      if (!stillMyTurn('entering')) return

      const clone = cloneWrapRef.current
      const arc = cloneArcRef.current
      const track = cloneTrackRef.current
      const slot = haloSlotRef.current
      if (clone == null || arc == null || track == null || slot == null) {
        finishInstantly()
        return
      }

      setBackdropVisible(true)

      const slotRect = slot.getBoundingClientRect()
      const dx = slotRect.left + slotRect.width / 2 - (measured.rect.left + measured.rect.width / 2)
      const dy = slotRect.top + slotRect.height / 2 - (measured.rect.top + measured.rect.height / 2)
      const circumference = 2 * Math.PI * measured.radius

      clone.style.filter = 'drop-shadow(0 0 10px var(--her-glow, transparent))'
      clone.style.transition = `transform 340ms ${EASE}`
      arc.style.transition = `stroke-dasharray 340ms ${EASE}, stroke 340ms ${EASE}`
      track.style.transition = `stroke 220ms ${EASE} 100ms, stroke-width 220ms ${EASE} 100ms`
      clone.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
      arc.style.strokeDasharray = `0 ${circumference}`
      arc.style.stroke = 'var(--her)'
      track.style.stroke = 'var(--her-glow, var(--g-line))'
      track.style.strokeWidth = '1.5'

      await sleep(340)
      if (!stillMyTurn('entering')) return

      setShowMark(true)
      setContentVisible(true)
      zoneSettled()

      clone.style.transition = `opacity 220ms ${EASE}`
      clone.style.opacity = '0'
      await sleep(220)
      // Phase is 'open' now, by our own hand a moment ago — that is the
      // correct phase to still find ourselves in for this last step.
      if (!stillMyTurn('open')) return
      setSnap(null)
    }

    void run()

    return () => {
      clearAll()
    }
    // Mount-only: this is the arrival, and it runs exactly once per visit.
  }, [])

  // ── The exit morph — fires once `beginCloseZone()` lands the phase on
  // 'leaving'; a no-op every other time `phase` changes. ──
  useEffect(() => {
    if (phase !== 'leaving') return
    // Invalidates a still-running enter sequence — see `runIdRef`'s comment
    // above — the moment this phase is reached, however far into its own
    // timers the enter morph still was.
    ++runIdRef.current
    let cancelled = false
    const { sleep, clearAll } = makeTimers()

    const hardExit = () => {
      if (cancelled) return
      cancelled = true
      clearAll()
      setSnap(null)
      setContentVisible(false)
      setShowMark(false)
      setBackdropVisible(false)
      closeZone()
    }
    skipRef.current = hardExit

    const run = async () => {
      const reduced = prefersReducedMotion()
      const target = reduced ? null : measureRing()
      const slot = haloSlotRef.current
      const startRect = slot?.getBoundingClientRect() ?? null
      // A measurement failure gets the same instant treatment as reduced
      // motion — there is nothing to fly the halo back toward, so the only
      // honest move is to just show/hide rather than fake an animation.
      const instant = reduced || target == null || startRect == null

      if (instant) {
        hardExit()
        return
      }

      const startRadius = lastSnapRef.current?.radius ?? target.radius
      const circumference = 2 * Math.PI * startRadius
      setSnap({
        rect: startRect,
        radius: startRadius,
        strokeWidth: 1.5,
        dasharray: `0 ${circumference}`,
        color: 'var(--her-glow, var(--g-line))',
      })
      setContentVisible(false)
      setShowMark(false)

      await sleep(32)
      if (cancelled) return
      const clone = cloneWrapRef.current
      const arc = cloneArcRef.current
      const track = cloneTrackRef.current
      if (clone == null || arc == null || track == null) {
        hardExit()
        return
      }
      clone.style.filter = 'drop-shadow(0 0 10px var(--her-glow, transparent))'

      const dx = target.rect.left - startRect.left
      const dy = target.rect.top - startRect.top

      clone.style.transition = `transform 280ms ${EASE}`
      track.style.transition = `stroke 280ms ${EASE}, stroke-width 280ms ${EASE}`
      arc.style.transition = `stroke-dasharray 280ms ${EASE}, stroke 280ms ${EASE}`
      clone.style.transform = `translate3d(${dx}px, ${dy}px, 0)`
      clone.style.filter = 'none'
      track.style.stroke = 'var(--g-line)'
      track.style.strokeWidth = String(target.strokeWidth)
      arc.style.stroke = target.color
      arc.style.strokeDasharray = target.dasharray

      await sleep(280)
      if (cancelled) return

      setBackdropVisible(false)
      await sleep(180)
      if (cancelled) return
      cancelled = true
      setSnap(null)
      closeZone()
    }

    void run()

    return () => {
      cancelled = true
      clearAll()
    }
  }, [phase])

  const onOverlayPointerDown = () => {
    if (phase === 'entering' || phase === 'leaving') skipRef.current()
  }

  const onHeroPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    if (event.target instanceof Element && event.target.closest('button') != null) return
    gestureRef.current = {
      id: event.pointerId,
      from: event.clientY,
      at: event.timeStamp,
      y: event.clientY,
      speed: 0,
      captured: false,
    }
  }

  const onHeroPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gestureRef.current
    if (current == null || current.id !== event.pointerId) return
    const elapsed = event.timeStamp - current.at
    if (elapsed > 0) current.speed = (event.clientY - current.y) / elapsed
    current.y = event.clientY
    current.at = event.timeStamp
    const travelled = Math.max(0, event.clientY - current.from)
    if (!current.captured && travelled > DRAG_START_PX) {
      current.captured = true
      event.currentTarget.setPointerCapture(event.pointerId)
    }
    setDrag(travelled)
  }

  const onHeroPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = gestureRef.current
    if (current == null || current.id !== event.pointerId) return
    gestureRef.current = null
    const travelled = Math.max(0, event.clientY - current.from)
    setDrag(0)
    if (phase !== 'open') return
    if (travelled > DISMISS_TRAVEL_PX) {
      beginCloseZone()
      return
    }
    if (travelled > DISMISS_FLICK_PX && current.speed > DISMISS_FLICK_SPEED) beginCloseZone()
  }

  if (openedDate == null || typeof document === 'undefined') return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Glitimė"
      data-zone-phase={phase}
      className="fixed inset-x-0 z-[35] flex flex-col"
      // Stops above the tab bar, same as the sheets do — `shell/Shell.tsx`
      // is what's showing there while the zone is open (see `DoorBar.tsx`),
      // not this overlay. Nothing about the door row rides on this box's own
      // positioning being pixel-perfect against Safari's viewport quirks.
      style={{ top: 'env(safe-area-inset-top, 0px)', bottom: 'var(--tabbar-h, 4.25rem)' }}
      onPointerDown={onOverlayPointerDown}
    >
      <div
        className="zone-backdrop absolute inset-0"
        data-visible={backdropVisible ? '1' : undefined}
      />

      {snap != null && (
        <div
          ref={cloneWrapRef}
          aria-hidden="true"
          className="zone-clone"
          style={{
            width: snap.rect.width,
            height: snap.rect.height,
            left: snap.rect.left,
            top: snap.rect.top,
          }}
        >
          <svg
            viewBox={`0 0 ${snap.rect.width} ${snap.rect.height}`}
            width={snap.rect.width}
            height={snap.rect.height}
            className="-rotate-90"
            fill="none"
            aria-hidden="true"
          >
            <circle
              ref={cloneTrackRef}
              cx={snap.rect.width / 2}
              cy={snap.rect.height / 2}
              r={snap.radius}
              fill="none"
              stroke="var(--g-line)"
              strokeWidth={snap.strokeWidth}
            />
            <circle
              ref={cloneArcRef}
              cx={snap.rect.width / 2}
              cy={snap.rect.height / 2}
              r={snap.radius}
              fill="none"
              stroke={snap.color}
              strokeWidth={snap.strokeWidth}
              strokeLinecap="round"
              strokeDasharray={snap.dasharray}
            />
          </svg>
        </div>
      )}

      <button
        type="button"
        onClick={beginCloseZone}
        aria-label="Uždaryti"
        className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-10 grid size-9 place-items-center rounded-full text-mute transition-colors hover:text-ink"
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="size-5">
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div
        className="zone-content relative flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-3"
        data-dragging={drag !== 0 ? '1' : undefined}
        style={{
          opacity: contentVisible ? 1 : 0,
          pointerEvents: contentVisible ? 'auto' : 'none',
          transform: drag !== 0 ? `translateY(${drag}px)` : undefined,
        }}
      >
        <div
          className="flex shrink-0 flex-col items-center gap-1 pt-4 pb-1 text-center"
          onPointerDown={onHeroPointerDown}
          onPointerMove={onHeroPointerMove}
          onPointerUp={onHeroPointerUp}
          onPointerCancel={onHeroPointerUp}
        >
          <div
            ref={haloSlotRef}
            className="grid place-items-center"
            style={{ width: HALO_SIZE, height: HALO_SIZE }}
          >
            {showMark && (
              <GlitimeMark size={BIG_MARK_SIZE} alive onPet={petHer} pose={pose} station="zone" />
            )}
          </div>
          {/* No name label: the mark, the colour and the room already say who
              this is — she can name herself in what she says instead. */}
          <p className="text-[0.6875rem] text-[var(--her)] uppercase tracking-[0.14em]">
            {TONE_STATUS[presence.tone]}
          </p>
        </div>

        {remark != null && (
          <p className="shrink-0 text-balance px-2 pb-1 text-center text-ink">
            <GhostReveal>{remark}</GhostReveal>
          </p>
        )}

        {herDay != null && (
          <section className="shrink-0 mb-2 w-full rounded-[var(--radius-card)] border border-line bg-raised px-3 py-2">
            <h3 className="mb-0.5 text-[0.6875rem] text-mute uppercase tracking-[0.14em]">
              Jos diena
            </h3>
            <p className="text-balance text-dim text-sm">{herDay}</p>
          </section>
        )}

        <div className="min-h-0 flex-1">
          {away != null ? (
            <DoorClosed
              state={away}
              onOpened={() => {
                // She came back on her own terms; the room is warm again and
                // the conversation starts from nothing — with one line about
                // where they left it.
                clearCooled()
                thawed.current = true
                setAway(null)
              }}
            />
          ) : (
            dayRecord != null &&
            opener !== undefined && (
              <ChatThread
                fill
                transport={transport}
                initialHistory={dayRecord.turns}
                onHistoryChange={persistTurns}
                onEnded={markChatEnded}
                onPose={setPose}
                opener={opener}
                quickReplies={chipLabels}
              />
            )
          )}
        </div>

        {openerFollowUpId != null && (
          <button
            type="button"
            onClick={() => {
              resolveFollowUpDone(openerFollowUpId)
              setOpenerFollowUpId(null)
            }}
            className="shrink-0 pt-2 pb-3 text-[0.6875rem] text-mute uppercase tracking-[0.14em] underline underline-offset-2"
          >
            Jau papasakojau
          </button>
        )}
      </div>

      <DossierSheet
        open={doorOpen === 'dossier'}
        onOpenChange={(open) => {
          if (!open) closeDoor()
        }}
      />
      <PromisesSheet
        open={doorOpen === 'promises'}
        onOpenChange={(open) => {
          if (!open) closeDoor()
        }}
      />
      <Sheet
        open={doorOpen === 'trophies'}
        onOpenChange={(open) => {
          if (!open) closeDoor()
        }}
        title="Trofėjai"
      >
        <div className="pb-4">
          <TrophyCabinet />
        </div>
      </Sheet>
    </div>,
    document.body,
  )
}
